import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext(null);

import { storeBackground } from "@/lib/backgrounds";

const DEFAULT_LOGOUT_MINUTES = 25;

const applyTheme = (t) => {
  document.documentElement.dataset.theme = t;
  try { localStorage.setItem("theme", t); } catch {}
};
try { document.documentElement.dataset.theme = localStorage.getItem("theme") || "sky"; } catch {}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoLogoutMin, setAutoLogoutMin] = useState(() => {
    try { return parseInt(localStorage.getItem("autoLogoutMin")) || DEFAULT_LOGOUT_MINUTES; } catch { return DEFAULT_LOGOUT_MINUTES; }
  });
  const [logoutWarning, setLogoutWarning] = useState(false);

  const logoutTimer = useRef(null);
  const warningTimer = useRef(null);
  const logoutRef = useRef(null);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND}/api/auth/me`, { withCredentials: true });
      setUser(res.data);
    } catch {
      setUser(false);
      // Clear emulation state if not authenticated
      localStorage.removeItem("emulating_from");
    }
    setLoading(false);
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  useEffect(() => { if (user?.theme) applyTheme(user.theme); }, [user?.theme]);
  useEffect(() => { if (user?.background) storeBackground(user.background); }, [user?.background]);

  const setBackground = async (background) => {
    storeBackground(background);
    setUser(u => (u ? { ...u, background } : u));
    await axios.put(`${BACKEND}/api/auth/background`, { background }, { withCredentials: true });
  };

  const setTheme = async (theme) => {
    applyTheme(theme);
    setUser(u => (u ? { ...u, theme } : u));
    await axios.put(`${BACKEND}/api/auth/theme`, { theme }, { withCredentials: true });
  };

  const clearDisclaimer = () => setUser(u => (u ? { ...u, disclaimer_pending: false } : u));
  const clearMustChangePassword = () => setUser(u => (u ? { ...u, must_change_password: false } : u));

  const completeTour = async () => {
    setUser(u => (u ? { ...u, tour_pending: false } : u));
    if (localStorage.getItem("emulating_from")) return;
    await axios.put(`${BACKEND}/api/auth/tour`, {}, { withCredentials: true }).catch(() => {});
  };

  const startEmulation = (adminEmail) => {
    localStorage.setItem("emulating_from", adminEmail);
  };

  const isEmulating = () => !!localStorage.getItem("emulating_from");

  const getEmulatingFrom = () => localStorage.getItem("emulating_from");

  const exitEmulation = async () => {
    const adminEmail = localStorage.getItem("emulating_from");
    if (!adminEmail) return;
    localStorage.removeItem("emulating_from");
    // Re-login as admin — use the emulate endpoint in reverse (admin re-auth)
    try {
      await axios.post(`${BACKEND}/api/admin/exit-emulation`, {}, { withCredentials: true });
      await checkAuth();
      window.location.href = "/admin";
    } catch {
      // Fallback: just redirect to login
      window.location.href = "/login";
    }
  };

  const login = async (email, password) => {
    const res = await axios.post(`${BACKEND}/api/auth/login`, { email: email.trim().toLowerCase(), password }, { withCredentials: true });
    setUser(res.data);
    return res.data;
  };

  const biometricLogin = async (email) => {
    const res = await axios.post(`${BACKEND}/api/auth/biometric-login`, { email: email.trim().toLowerCase() }, { withCredentials: true });
    setUser(res.data);
    return res.data;
  };

  const register = async (email, password, name) => {
    const res = await axios.post(`${BACKEND}/api/auth/register`, { email: email.trim().toLowerCase(), password, name }, { withCredentials: true });
    setUser(res.data);
    return res.data;
  };

  const logout = useCallback(async () => {
    try {
      await axios.post(`${BACKEND}/api/auth/logout`, {}, { withCredentials: true });
    } catch {}
    setUser(false);
    setLogoutWarning(false);
  }, []);

  // Keep ref updated for timer callbacks
  useEffect(() => { logoutRef.current = logout; }, [logout]);

  const updateAutoLogoutMin = useCallback((mins) => {
    setAutoLogoutMin(mins);
    try { localStorage.setItem("autoLogoutMin", mins.toString()); } catch {}
  }, []);

  const resetInactivity = useCallback(() => {
    setLogoutWarning(false);
    if (logoutTimer.current) clearTimeout(logoutTimer.current);
    if (warningTimer.current) clearTimeout(warningTimer.current);
    const mins = autoLogoutMin || DEFAULT_LOGOUT_MINUTES;
    warningTimer.current = setTimeout(() => setLogoutWarning(true), (mins - 1) * 60 * 1000);
    logoutTimer.current = setTimeout(() => {
      if (logoutRef.current) logoutRef.current();
    }, mins * 60 * 1000);
  }, [autoLogoutMin]);

  // Auto-logout: listen for activity globally when logged in
  useEffect(() => {
    if (!user) {
      // Clear timers when logged out
      if (logoutTimer.current) clearTimeout(logoutTimer.current);
      if (warningTimer.current) clearTimeout(warningTimer.current);
      setLogoutWarning(false);
      return;
    }
    resetInactivity();
    const events = ["mousedown", "keydown", "scroll", "touchstart", "mousemove"];
    // Throttle: only reset every 10 seconds to avoid constant timer resets
    let lastReset = Date.now();
    const throttled = () => {
      if (Date.now() - lastReset > 10000) {
        lastReset = Date.now();
        resetInactivity();
      }
    };
    events.forEach(e => window.addEventListener(e, throttled, { passive: true }));
    return () => {
      events.forEach(e => window.removeEventListener(e, throttled));
      if (logoutTimer.current) clearTimeout(logoutTimer.current);
      if (warningTimer.current) clearTimeout(warningTimer.current);
    };
  }, [user, resetInactivity]);

  return (
    <AuthContext.Provider value={{
      user, loading, login, biometricLogin, register, logout, checkAuth,
      autoLogoutMin, updateAutoLogoutMin, logoutWarning, resetInactivity,
      startEmulation, isEmulating, getEmulatingFrom, exitEmulation, setTheme, setBackground, completeTour, clearMustChangePassword, clearDisclaimer
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
